import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  width: '100%',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  border: `2px dashed ${theme.palette.grey[300]}`,
}));

interface CustomEmptyStateProps {
  resource: string;
  title?: string;
  subtitle?: string;
  actionText?: string;
  onAction?: () => void;
  icon?: React.ReactNode;
  showBackground?: boolean;
}

const getResourceDetails = (resource: string) => {
  switch (resource) {
    case 'drivers':
      return {
        icon: <DriveEtaIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Drivers Yet',
        subtitle: 'Start building your driver database by adding your first driver profile. You can manage licenses, certifications, and employment history.',
        actionText: 'Create Driver Profile',
      };
    case 'users':
      return {
        icon: <PersonAddIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Users Yet',
        subtitle: 'Add users to your system to manage access and permissions. Users can be drivers, administrators, or other team members.',
        actionText: 'Add User',
      };
    case 'equipment':
      return {
        icon: <BuildIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Equipment Yet',
        subtitle: 'Track your fleet equipment, vehicles, and assets. Manage maintenance schedules and equipment assignments.',
        actionText: 'Add Equipment',
      };
    case 'forms':
      return {
        icon: <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Forms Yet',
        subtitle: 'Create custom forms for data collection, inspections, and documentation. Build your workflow with digital forms.',
        actionText: 'Create Form',
      };
    case 'completed-forms':
      return {
        icon: <AssignmentIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Completed Forms',
        subtitle: 'Form submissions and completed responses will appear here. Track progress and review submitted data.',
        actionText: 'View Forms',
      };
    case 'articles':
      return {
        icon: <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Articles Yet',
        subtitle: 'Share knowledge and updates with your team. Create articles for policies, procedures, and announcements.',
        actionText: 'Write Article',
      };
    default:
      return {
        icon: <FolderOpenIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: `No ${resource.charAt(0).toUpperCase() + resource.slice(1)} Yet`,
        subtitle: `Start by adding your first ${resource.replace('-', ' ')} item.`,
        actionText: `Add ${resource.charAt(0).toUpperCase() + resource.slice(1)}`,
      };
  }
};

export const CustomEmptyState: React.FC<CustomEmptyStateProps> = ({
  resource,
  title,
  subtitle,
  actionText,
  onAction,
  icon,
  showBackground = true,
}) => {
  const defaults = getResourceDetails(resource);
  
  const finalTitle = title || defaults.title;
  const finalSubtitle = subtitle || defaults.subtitle;
  const finalActionText = actionText || defaults.actionText;
  const finalIcon = icon || defaults.icon;

  const content = (
    <EmptyStateContainer>
      <IconContainer>
        {finalIcon}
      </IconContainer>
      
      <Typography 
        variant="h5" 
        component="h2" 
        gutterBottom
        sx={{ 
          fontWeight: 600,
          color: 'text.primary',
          mb: 2,
        }}
      >
        {finalTitle}
      </Typography>
      
      <Typography 
        variant="body1" 
        color="text.secondary"
        sx={{ 
          maxWidth: 480,
          lineHeight: 1.6,
          mb: 4,
        }}
      >
        {finalSubtitle}
      </Typography>
      
      {onAction && (
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={onAction}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          {finalActionText}
        </Button>
      )}
    </EmptyStateContainer>
  );

  if (showBackground) {
    return (
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            maxWidth: '600px',
            width: '100%',
            margin: 'auto',
          }}
        >
          {content}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh'
    }}>
      {content}
    </Box>
  );
};